import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSalesDocument, updateSalesDocument } from '../redux/actions/salesDocumentActions';
import { fetchFiles } from '../redux/actions/fileActions';
import { fetchRelationships } from '../redux/actions/relationshipActions';
import { listTimesheets } from '../redux/actions/timesheetActions';
import CreateProvisionPage from './CreateProvisionPage';
import { Box, CircularProgress } from '@mui/material';

const ViewEditProvisionPage = () => {
  const { t } = useTranslation(['common', 'pages']);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const { salesDocument, loading, error } = useSelector((state) => state.salesDocumentDetails);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { timesheets } = useSelector((state) => state.timesheetList);
  const { files } = useSelector((state) => state.fileList);

  // Reset salesDocument when component unmounts
  useEffect(() => {
    return () => {
      dispatch({ type: 'SALESDOCUMENT_DETAILS_RESET' });
      setHasFetched(false);
    };
  }, [dispatch]);

  // Fetch necessary data
  useEffect(() => {
    // Prevent multiple fetches for the same ID
    if (hasFetched && salesDocument?._id === id) {
      return;
    }

    const fetchData = async () => {
      // Only reset if we're fetching a different document
      if (salesDocument?._id !== id) {
        dispatch({ type: 'SALESDOCUMENT_DETAILS_RESET' });
      }
      
      // Always fetch timesheets to ensure we have complete data for filtering
      // Only fetch other data if it doesn't exist in Redux store
      const promises = [];
      promises.push(dispatch(listTimesheets())); // Always fetch timesheets
      if (!files || files.length === 0) {
        promises.push(dispatch(fetchFiles()));
      }
      if (!relationships || relationships.length === 0) {
        promises.push(dispatch(fetchRelationships()));
      }

      // Wait for any necessary data fetches
      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Fetch sales document only if we don't already have it
      if (id && salesDocument?._id !== id) {
        await dispatch(getSalesDocument(id));
        setHasFetched(true);
      } else if (salesDocument?._id === id) {
        setHasFetched(true);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    if (salesDocument && salesDocument?.items && relationships?.length > 0) {
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date) 
          ? date.toISOString().split('T')[0]
          : '';
      };

      // Handle different ways the API might return from/to (either as objects or as IDs)
      const fromId = typeof salesDocument.from === 'object' ? salesDocument.from?._id : salesDocument.from;
      const toId = typeof salesDocument.to === 'object' ? salesDocument.to?._id : salesDocument.to;
      const caseId = typeof salesDocument.case === 'object' ? salesDocument.case?._id : salesDocument.case;

      console.log("ViewEditProvisionPage - Raw salesDocument:", salesDocument);
      console.log("ViewEditProvisionPage - fromId:", fromId);
      console.log("ViewEditProvisionPage - toId:", toId);
      console.log("ViewEditProvisionPage - caseId:", caseId);

      setInitialData({
        ...salesDocument,
        case: caseId || '',
        from: fromId || '',
        to: toId || '',
        date: formatDate(salesDocument.date),
        dueDate: formatDate(salesDocument.dueDate),
        items: salesDocument.items.map(item => ({
          ...item,
          value: Number(item.value) || 0,
        })),
        total: Number(salesDocument.total) || 0,
        totalDue: Number(salesDocument.totalDue) || 0,
        VAT: Number(salesDocument.VAT) || 0
      });

      console.log("ViewEditProvisionPage - Setting initialData:", {
        case: caseId || '',
        from: fromId || '',
        to: toId || ''
      });
    }
  }, [salesDocument, relationships, timesheets]);

  if (loading || !initialData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <div>Error: {error}</div>;

  return (
    <CreateProvisionPage
      isEditing={true}
      initialData={initialData}
      onSubmit={(formData) => {
        dispatch(updateSalesDocument(id, formData));
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }}
    />
  );
};

export default ViewEditProvisionPage; 